import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import type { Group } from 'models/flexible/Group';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  aiCriteriaSetsQueryKeys,
  createAICriteriaSet,
  indexAICriteriaSets,
  showAICriteriaSet,
  updateAICriteriaSet,
} from 'services/api/admin/aiCriteriaSets';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import type { AdminGroupIndexFields } from 'services/api/fields/admin/group';

import CategoriesList, { type CriteriaItem, generateTempCategoryId } from './CategoriesList';
import { buildCriteriaSetPayload, categoryNamesForDupGuard, isMeaninglessNewGroupSet } from './criteriaSetPayload';
import { editAICriteriaSetValidation } from './editAICriteriaSetValidation';
import type { CriteriaCategory } from './NewCategoryModal';

const getId = (o: any) => o?.id as string;
const getName = (o: any) => o?.name as string;

type ScopeableGroup = Group<AdminGroupIndexFields<'groupType'>>;

type FormValues = {
  client: { id: string; name: string } | null;
  group: ScopeableGroup | null;
  categories: CriteriaCategory[];
  aiCriteria: CriteriaItem[];
  hiddenAiCriteriaIds: string[];
  inheritedCategories: CriteriaCategory[];
  inheritedCriteria: CriteriaItem[];
};

// Shared with toFormValues below -- maps raw API category/criteria rows onto the read-only
// "inherited" form shape, whether they came from the record's own show payload (edit mode) or
// from a fetched client-level set (create/copy mode preview).
//
// _tempId is derived deterministically from the category's stable `id` (inherited categories
// always have a real id) rather than randomly generated, so a background refetch of the same
// data doesn't churn the _tempId that CategoriesList uses to key an open slideout.
function mapInheritedCategories(categories: any[] | undefined): CriteriaCategory[] {
  return (categories ?? [])
    .map((c: any) => ({
      _tempId: `_temp_cat_${c.id}`,
      id: c.id,
      name: c.name,
      color: c.color,
      order: c.order,
    }))
    .sort((a: CriteriaCategory, b: CriteriaCategory) => a.order - b.order);
}

function mapInheritedCriteria(criteria: any[] | undefined): CriteriaItem[] {
  return (criteria ?? [])
    .filter((c: any) => !c.archived)
    .map((c: any) => ({
      id: c.id,
      label: c.label,
      description: c.description,
      category: c.category,
      categoryId: c.categoryId,
      order: c.order,
    }))
    .sort((a: CriteriaItem, b: CriteriaItem) => a.order - b.order);
}

// Decides a copied criteria's categoryId. Copies are new records, so an own-category criteria's
// id is dropped (the category is recreated and resolved by name on save). A group copy stays under
// the same client, so a criteria filed under an INHERITED client category keeps that id -- it's
// still valid and the API permits a group criteria under its inherited client category.
function copyCategoryId(
  criteria: { categoryId?: string },
  ctx: { isCopyMode: boolean; isGroupCopy: boolean; inheritedCategoryIds: Set<string> }
): string | undefined {
  if (!ctx.isCopyMode) return criteria.categoryId; // edit mode: unchanged
  if (ctx.isGroupCopy && criteria.categoryId && ctx.inheritedCategoryIds.has(criteria.categoryId)) {
    return criteria.categoryId;
  }
  return undefined;
}

export default function EditAICriteriaSet() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isCopyMode = searchParams.get('copy') === 'true';
  const isEditing = !!id && !isCopyMode;
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminAICriteriaEdit);
  const readOnly = isEditing && !canEdit;

  const form = useAdminForm({
    id,
    show: showAICriteriaSet,
    include: ['group', 'categories', 'criteria', 'client'] as const,
    queryKey: aiCriteriaSetsQueryKeys,
    schema: editAICriteriaSetValidation,
    create: (payload: any) => createAICriteriaSet(payload),
    update: isCopyMode
      ? (_id: string, payload: any) => createAICriteriaSet(payload)
      : (_id: string, payload: any) => updateAICriteriaSet({ id: _id, ...payload }),
    toFormValues: (data: any): FormValues => {
      if (!data) {
        return {
          client: null,
          group: null,
          categories: [],
          aiCriteria: [],
          hiddenAiCriteriaIds: [],
          inheritedCategories: [],
          inheritedCriteria: [],
        };
      }

      // Copy mode branches on the source set's scope. Duplicating a GROUP set stays within the same
      // client (only the group is re-picked), so the client is retained and the inherited context is
      // rebuilt by the create-mode preview effect below. Duplicating a CLIENT set targets a new
      // client, so the client is cleared. Either way the copy is a new record: strip persisted ids.
      const isGroupCopy = isCopyMode && !!data.group;
      const isClientCopy = isCopyMode && !isGroupCopy;
      // Only consulted for a group copy (see copyCategoryId), so only built then.
      const inheritedCategoryIds = isGroupCopy
        ? new Set<string>((data.inheritedCategories ?? []).map((c: any) => c.id))
        : new Set<string>();

      return {
        client: isClientCopy ? null : (data.client ?? null),
        group: isCopyMode ? null : (data.group ?? null),
        categories: (data.categories ?? [])
          .map((c: any) => ({
            _tempId: generateTempCategoryId(),
            id: isCopyMode ? undefined : c.id,
            name: c.name,
            color: c.color,
            order: c.order,
          }))
          .sort((a: CriteriaCategory, b: CriteriaCategory) => a.order - b.order),
        aiCriteria: (data.criteria ?? [])
          .filter((c: any) => !c.archived)
          .map((c: any) => ({
            id: isCopyMode ? undefined : c.id,
            label: c.label,
            description: c.description,
            category: c.category,
            categoryId: copyCategoryId(c, { isCopyMode, isGroupCopy, inheritedCategoryIds }),
            order: c.order,
          }))
          .sort((a: CriteriaItem, b: CriteriaItem) => a.order - b.order),
        hiddenAiCriteriaIds: isClientCopy ? [] : (data.hiddenAiCriteriaIds ?? []),
        // Copy mode leaves inherited empty here; for a group copy the preview effect repopulates it
        // from the retained client (edit mode still reads it straight from the show payload).
        inheritedCategories: isCopyMode ? [] : mapInheritedCategories(data.inheritedCategories),
        inheritedCriteria: isCopyMode ? [] : mapInheritedCriteria(data.inheritedCriteria),
      };
    },
    toPayload: (values: FormValues) =>
      buildCriteriaSetPayload(
        {
          client: values.client,
          group: values.group ? { id: values.group.id, name: values.group.name } : null,
          categories: values.categories,
          aiCriteria: values.aiCriteria,
          hiddenAiCriteriaIds: values.hiddenAiCriteriaIds,
        },
        { isEditing }
      ),
    onCreateSuccess: { toast: 'Clinical criteria successfully created.', navigate: '/admin/admissions/criteria' },
    onUpdateSuccess: {
      toast: isCopyMode ? 'Clinical criteria successfully created.' : 'Clinical criteria successfully updated.',
      navigate: '/admin/admissions/criteria',
    },
    onError: { toast: 'There was an error saving the clinical criteria. Please try again.' },
  });

  // A group copy duplicates a group set within its client, so the client selector is locked. Derive
  // this from the source set's scope (stable), not the live client field -- a client copy populates
  // that field once the admin picks a target, and that must not lock them back out.
  const isGroupCopy = isCopyMode && !!form.data?.group;

  // Reset group when client changes (but not on initial data load)
  const prevClientIdRef = useRef(form.values.client?.id);
  const isInitialClientLoad = useRef(true);

  useEffect(() => {
    if (prevClientIdRef.current === form.values.client?.id) return;
    prevClientIdRef.current = form.values.client?.id;

    // Skip resetting group on the initial data load so we don't
    // trigger a validation error before the user has interacted.
    if (isInitialClientLoad.current) {
      isInitialClientLoad.current = false;
      return;
    }

    form.setValue('group', null);
  }, [form.values.client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'aiCriteriaSets'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'aiCriteriaSets', form.values.client?.id],
    fetchFn: (params, signal) => adminIndexGroups(params, signal, ['groupType']),
    params: {
      clientId: form.values.client?.id,
      isProvider: true,
      sortBy: 'name asc',
    },
  });

  // In create/copy mode, every group under a client inherits the same client-level AI criteria
  // set, so as soon as a client is picked we can preview what a new group set will inherit.
  // Editing an existing record is untouched -- its inherited data comes from the show payload
  // (see toFormValues) since the record's own scope determines what it actually inherits.
  const selectedClientId = form.values.client?.id;

  const inheritedClientSetQuery = useQuery({
    queryKey: ['adminAICriteriaSets', 'clientInheritedPreview', selectedClientId],
    queryFn: async ({ signal }) => {
      const { data } = await indexAICriteriaSets({ clientId: selectedClientId, pageSize: 100 }, signal, ['client']);
      const clientSet = data.find((s) => s.scopeableType === 'Client');
      if (!clientSet) return null;
      return showAICriteriaSet(clientSet.id, signal, ['categories', 'criteria'] as const);
    },
    enabled: !isEditing && !!selectedClientId,
    // The query key includes the selected client id, so a genuine client change still refetches.
    // A large staleTime + disabling refetch-on-focus keeps the resulting data reference stable
    // for the life of a given client selection, avoiding needless re-maps (see mapInheritedCategories).
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isEditing) return;

    const clientSet = selectedClientId ? inheritedClientSetQuery.data : null;

    form.setValues(
      {
        inheritedCategories: mapInheritedCategories(clientSet?.categories),
        inheritedCriteria: mapInheritedCriteria(clientSet?.criteria),
      },
      { validate: false }
    );
    // Deliberately re-runs (and clears, then repopulates) whenever the selected client changes,
    // coordinating with the group-reset effect above which fires on the same dependency.
  }, [isEditing, selectedClientId, inheritedClientSetQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track categories/criteria in a local state pair so we can pass setters to CategoriesList
  // and still keep them in sync with the form hook.
  const [localCategories, setLocalCategories] = useState<CriteriaCategory[]>(form.values.categories);
  const [localCriteria, setLocalCriteria] = useState<CriteriaItem[]>(form.values.aiCriteria);

  // Sync from form -> local when form reinitializes (e.g. data fetch completes)
  const prevFormCategoriesRef = useRef(form.values.categories);
  const prevFormCriteriaRef = useRef(form.values.aiCriteria);

  useEffect(() => {
    if (prevFormCategoriesRef.current !== form.values.categories) {
      prevFormCategoriesRef.current = form.values.categories;
      setLocalCategories(form.values.categories);
    }
    if (prevFormCriteriaRef.current !== form.values.aiCriteria) {
      prevFormCriteriaRef.current = form.values.aiCriteria;
      setLocalCriteria(form.values.aiCriteria);
    }
  }, [form.values.categories, form.values.aiCriteria]);

  // Sync from local -> form when local state changes
  const handleCategoriesChange = (cats: CriteriaCategory[]) => {
    setLocalCategories(cats);
    form.setValue('categories', cats);
  };

  const handleCriteriaChange = (crit: CriteriaItem[]) => {
    setLocalCriteria(crit);
    form.setValue('aiCriteria', crit);
  };

  // A brand-new group-scoped set with no own criteria and nothing hidden from the inherited list
  // is a no-op -- the group already inherits the client's full list without saving a set of its
  // own. Blocked only for a new (create/copy) group-scoped set; editing, and new client-only
  // sets, are unaffected.
  const blockEmptyGroupSet = isMeaninglessNewGroupSet(
    {
      group: form.values.group,
      aiCriteria: form.values.aiCriteria,
      hiddenAiCriteriaIds: form.values.hiddenAiCriteriaIds,
    },
    { isEditing }
  );

  if (id && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <>
        <FormHeader title={isEditing ? 'Edit Clinical Criteria' : 'Add Clinical Criteria'} />

        <InputGroup title='Client' error={getFieldError(form.errors, 'client')}>
          <Select
            {...clientOpts}
            value={form.values.client}
            onChange={(v) => form.setValue('client', v)}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Client'
            isDisabled={isEditing || readOnly || isGroupCopy}
            dataTestId='criteria-form-client'
          />
        </InputGroup>

        <InputGroup title='Group' error={getFieldError(form.errors, 'group')}>
          <Select<ScopeableGroup>
            {...groupOpts}
            value={form.values.group}
            onChange={(v) => form.setValue('group', v)}
            getOptionValue={getId}
            getOptionLabel={(o) => (o?.groupType?.displayName ? `${o.name} (${o.groupType.displayName})` : o?.name)}
            placeholder='Group'
            isDisabled={isEditing || readOnly || !form.values.client}
            dataTestId='criteria-form-group'
          />
        </InputGroup>

        <CategoriesList
          categories={localCategories}
          criteria={localCriteria}
          inheritedCategories={form.values.inheritedCategories}
          inheritedCriteria={form.values.inheritedCriteria}
          hiddenAiCriteriaIds={form.values.hiddenAiCriteriaIds}
          onHiddenChange={(ids) => form.setValue('hiddenAiCriteriaIds', ids)}
          existingCategoryNames={categoryNamesForDupGuard(localCategories, form.values.inheritedCategories)}
          cascadesToGroups={!!form.values.client && !form.values.group}
          onCategoriesChange={handleCategoriesChange}
          onCriteriaChange={handleCriteriaChange}
        />

        <StyledButtonGroup>
          <Button variant='ghost' onClick={form.goBack}>
            Cancel
          </Button>
          <Button
            onClick={form.submit}
            disabled={readOnly || !form.valid || form.submitting || blockEmptyGroupSet}
            loading={form.submitting}>
            {isEditing ? 'Update Clinical Criteria' : 'Create Clinical Criteria'}
          </Button>
        </StyledButtonGroup>
      </>
    </AdminFormPage>
  );
}

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: 40px;
  justify-content: flex-end;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
